import React, { useState, useMemo, useEffect } from 'react';
import {
	Box,
	Flex,
	HStack,
	Icon,
	Progress,
	Text,
	Collapse,
	Badge,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp, FaServer } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDomains } from '../../../hooks/useDomain';
import { useSenders } from '../../../hooks/useSender';
import { useSubuserStats } from '../../../hooks/useSendGridSubuser';
import { useEmailCompliance } from '../../../hooks/useEmailCompliance';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import { emailSetupStepUrl } from '../../../utils/emailSetupDeepLink';
import {
	buildEmailSetupSteps,
	getEmailSetupProgress,
	isEmailServiceReady,
} from '../../../utils/emailSetupSteps';
import EmailSetupStepCard from '../../SendGrid/EmailSetupStepCard';

const OnboardingChecklist = () => {
	const navigate = useNavigate();
	const { companyId, isCompanyAdmin, userRole } = useAuthenticatedUserInfo();
	const canManageSetup = isCompanyAdmin;

	const [collapsed, setCollapsed] = useState(false);
	const [pollCompliance, setPollCompliance] = useState(false);
	const [pollSubuser, setPollSubuser] = useState(false);

	const { data: domains = [], isLoading: domainsLoading } =
		useDomains(companyId);
	const { data: senders = [], isLoading: sendersLoading } =
		useSenders(companyId);
	const { data: complianceResponse, isLoading: complianceLoading } =
		useEmailCompliance(companyId, {
			refetchInterval: pollCompliance ? 5000 : false,
		});

	const compliance = complianceResponse?.compliance;
	const isComplianceApproved = compliance?.vettingStatus === 'approved';
	const isComplianceSubmitted = Boolean(compliance?.submittedAt);

	const { data: subuserResponse, isLoading: subuserLoading } = useSubuserStats(
		companyId,
		{
			refetchInterval: pollSubuser ? 3000 : false,
		},
	);

	useEffect(() => {
		const c = complianceResponse?.compliance;
		setPollCompliance(
			Boolean(c?.submittedAt) && c?.vettingStatus !== 'approved',
		);
	}, [complianceResponse]);

	useEffect(() => {
		setPollSubuser(
			compliance?.vettingStatus === 'approved' &&
				!isEmailServiceReady(subuserResponse?.stats),
		);
	}, [compliance?.vettingStatus, subuserResponse]);

	const isLoading =
		domainsLoading || sendersLoading || subuserLoading || complianceLoading;

	const subuser = subuserResponse?.stats;
	const hasEmailServiceReady = isEmailServiceReady(subuser);

	const hasVerifiedDomain = domains.some((d) => d.verified === true);
	const hasSender = senders.length > 0;

	const steps = useMemo(
		() =>
			buildEmailSetupSteps({
				isComplianceApproved,
				isComplianceSubmitted,
				hasEmailServiceReady,
				hasVerifiedDomain,
				hasSender,
			}),
		[
			isComplianceApproved,
			isComplianceSubmitted,
			hasEmailServiceReady,
			hasVerifiedDomain,
			hasSender,
		],
	);

	const { completedCount, allDone, activeStepId, actionableStepId, progressPercent } =
		getEmailSetupProgress(steps);

	const isRelevantUser =
		userRole === 'Company Admin' || userRole === 'Employee';

	if (!isRelevantUser || isLoading || allDone) return null;

	const goToStep = (stepId) => navigate(emailSetupStepUrl(stepId));

	const goToSettings = () => {
		if (actionableStepId) goToStep(actionableStepId);
	};

	return (
		<Box
			bg='white'
			border='1px solid'
			borderColor='orange.200'
			borderRadius='lg'
			shadow='sm'
			overflow='hidden'>
			<Flex
				px={5}
				py={3}
				align='center'
				justify='space-between'
				bg='orange.50'
				cursor='pointer'
				onClick={() => setCollapsed((c) => !c)}>
				<HStack spacing={3}>
					<Icon as={FaServer} color='orange.400' boxSize={4} />
					<Text fontWeight='700' fontSize='sm' color='gray.800'>
						Complete your email setup to start sending campaigns
					</Text>
					<Badge colorScheme='orange' borderRadius='full' fontSize='xs' px={2}>
						{steps.length - completedCount} remaining
					</Badge>
				</HStack>
				<Icon
					as={collapsed ? FaChevronDown : FaChevronUp}
					color='gray.400'
					boxSize={3}
				/>
			</Flex>

			<Collapse in={!collapsed} animateOpacity>
				<Box px={5} pb={4} pt={3}>
					<Flex align='center' gap={3} mb={4}>
						<Progress
							value={progressPercent}
							colorScheme='orange'
							size='sm'
							borderRadius='full'
							flex={1}
							bg='gray.100'
						/>
						<Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
							{completedCount} / {steps.length} complete
						</Text>
					</Flex>

					<Flex
						gap={2}
						align='stretch'
						overflowX='auto'
						pb={1}
						css={{
							'&::-webkit-scrollbar': { height: '4px' },
							'&::-webkit-scrollbar-thumb': {
								background: '#CBD5E0',
								borderRadius: '4px',
							},
						}}>
						{steps.map((step, index) => (
							<React.Fragment key={step.id}>
								<EmailSetupStepCard
									step={step.step}
									shortLabel={step.shortLabel}
									isComplete={step.done}
									isWaiting={step.waiting}
									waitingLabel={step.waitingLabel}
									isActive={step.id === activeStepId}
									isLocked={!step.unlocked}
									onClick={
										canManageSetup && step.id === actionableStepId
											? () => goToStep(step.id)
											: undefined
									}
								/>
								{index < steps.length - 1 && (
									<Box
										display={{ base: 'none', md: 'flex' }}
										alignItems='center'
										color='gray.300'
										fontSize='lg'
										px={1}
										flexShrink={0}>
										›
									</Box>
								)}
							</React.Fragment>
						))}
					</Flex>

					<Text fontSize='xs' color='gray.400' mt={3} textAlign='center'>
						{canManageSetup ? (
							actionableStepId ? (
								<>
									Continue setup in{' '}
									<Text
										as='span'
										color='orange.500'
										fontWeight='600'
										cursor='pointer'
										onClick={goToSettings}
										_hover={{ textDecoration: 'underline' }}>
										Settings → Domains &amp; Senders
									</Text>
								</>
							) : isComplianceApproved && !hasEmailServiceReady ? (
								'Compliance approved — your email sending account is activating automatically.'
							) : (
								'Your compliance questionnaire is pending review — no action needed right now.'
							)
						) : (
							'Ask your company admin to complete email setup in Settings → Domains & Senders.'
						)}
					</Text>
				</Box>
			</Collapse>
		</Box>
	);
};

export default OnboardingChecklist;
